import { z } from 'zod';
import { evaluateExpression, validateCSVFormat, parseCSVLine } from '../shared';
import { validateRebarFormat } from '../shared/utils/inputUtils';

/**
 * Base schema for individual field validation
 * Keep ALL input values as strings (since inputs emit strings),
 * and let Zod coerce/validate.
 */
export const baseSchema = z.object({
  project: z.string().min(1, 'Project is required'),
  title: z.string().min(1, 'Title is required'),
  length: z.coerce.number().gt(0, 'Length must be at greater than 0'),
  tributaryWidth: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0.0;
  }, 'Tributary Width must be a valid expression greater than 0'),
  addedWeight: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0.0;
  }, 'Added Weight must be a valid expression greater than or equal to 0 and 1000 psf'),

  // Dropdown fields
  supports: z.coerce.number().int().min(1, 'Support type is required').max(6, 'Support type must be between 1 and 6'),
  orientation: z.coerce.number().int().min(0).max(1, 'Orientation is required'),

  sectionType: z.coerce.number().int().min(0).max(2, 'Section type is required'),

  b: z.coerce.number().gt(0.0, 'b must be greater than 0'),
  h: z.coerce.number().gt(0.0, 'h must be greater than 0'),
  tf: z.coerce.number().gte(0, 'tf must be at least 0'),
  tw: z.coerce.number().gte(0, 'tw must be at least 0'),

  rebar: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  rebarDepth: z.string().superRefine((val, ctx) => {
    const err = validateRebarFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  shearRebar: z.string().regex(/^\d-n\d@\d+$/, {
    message: 'Expected format like 2-n4@4'
  }),

  fy: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 1 && n <= 100000;
  }, 'Fy must be a valid expression between 1 and 100000'),

  fc: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 1 && n <= 10000;
  }, 'Fc must be a valid expression between 1 and 10000'),

  dif: z.coerce.number().int().min(0).max(2, 'DIF type is required'),

  axialLoad: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Axial Load must be a valid expression greater than or equal to 0'),

  pressureHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  timeHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  u0: z.coerce.number().gte(0, 'u0 must be at least 0'),
  v0: z.coerce.number().gte(0, 'v0 must be at least 0')
});

/**
 * Full schema with cross-field validation
 */
export const schema = baseSchema.superRefine((data, ctx) => {
  if (parseCSVLine(data.pressureHistory)?.length !== parseCSVLine(data.timeHistory)?.length) {
    const msg = 'Pressure and time histories must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['pressureHistory'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['timeHistory'] });
  }

  if (parseCSVLine(data.rebar)?.length !== parseCSVLine(data.rebarDepth)?.length) {
    const msg = 'Rebar and rebar diameter entries must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['rebar'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['rebarDiameter'] });
  }
});

/**
 * Helper types
 */
export type FieldName = keyof z.infer<typeof baseSchema>;
export type FormValues = z.input<typeof schema>; // raw form shape (strings from inputs/selects)
export type ParsedValues = z.output<typeof schema>; // parsed shape (numbers after coercion)

export type FormErrors = Partial<Record<FieldName, string | null>>;

/**
 * Validate a single field
 */
export function validateOneField<K extends FieldName>(key: K, raw: unknown): string | null {
  const fieldSchema = baseSchema.shape[key];
  const res = fieldSchema.safeParse(raw);
  return res.success ? null : (res.error.issues[0]?.message ?? 'Invalid value');
}
